import React from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  Grid,
  Stack,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Header from "../../components/Header";

const Card = ({ title, children }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.custom.serviceCardBackground,
        border: `1px solid ${isDark ? "#2f3b52" : "#d8d8d8"}`,
        borderRadius: 2,
        p: 2,
        boxShadow: isDark ? "0 6px 16px rgba(0,0,0,0.35)" : "0 10px 24px rgba(15,23,42,0.08)",
        height: "100%",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
};

const BulletList = ({ items }) => (
  <Box component="ul" sx={{ pl: 3, m: 0 }}>
    {items.map((item, idx) => (
      <Typography key={idx} component="li" sx={{ mb: 0.5 }}>
        {item}
      </Typography>
    ))}
  </Box>
);

const HelpPage = () => {
  const theme = useTheme();

  const guiasRapidas = [
    {
      title: "Para administradores (Boss / LicenseUser)",
      bullets: [
        "Revisa el Panel de Control para KPIs rápidos y ranking de conductores.",
        "Da de alta conductores, licencias y vehículos desde el menú Administración.",
        "Controla las hojas de turno pendientes, liquídalas y consulta las liquidaciones consolidadas.",
        "Usa Analítica y Mapa de calor para detectar zonas y horarios rentables.",
      ],
      chip: "Gestión diaria",
    },
    {
      title: "Para conductores",
      bullets: [
        "Crea tu hoja desde Hojas de Turno > Añadir turno e introduce recaudación, kilómetros y gasolina.",
        "Carga servicios manualmente o usando el botón “Importar CSV” si tienes extracto del taxímetro.",
        "Liquida tus hojas seleccionándolas (mismo conductor) y consulta Turnos Liquidados o Liquidaciones.",
        "Mantén tu perfil actualizado en Mi Usuario para que las liquidaciones muestren tus datos.",
      ],
      chip: "Operativa",
    },
    {
      title: "Control de datos",
      bullets: [
        "Servicios abonados muestra el histórico de viajes y permite buscar por dirección o fecha.",
        "Vehículos y mantenimientos centralizan matrículas, kilometraje y próxima revisión.",
        "Las licencias se asocian a conductores y turnos; mantenlas al día antes de crear turnos.",
        "Las descargas/exportaciones se realizan desde los listados (icono de menú o acciones).",
      ],
      chip: "Dónde mirar",
    },
  ];

  const flujos = [
    {
      title: "Crear una hoja de turno",
      steps: [
        "Entra en Hojas de Turno y pulsa “Añadir turno”.",
        "Completa datos de inicio y fin: recaudación, tarjeta, crédito, gasolina y kilómetros.",
        "Añade servicios individualmente o pega un CSV/archivo desde el botón de importación; se rellenan automáticamente.",
        "Guarda: el turno quedará en la lista de pendientes hasta que lo liquides.",
      ],
    },
    {
      title: "Liquidar hojas de turno",
      steps: [
        "Opcional: filtra por conductor para ver solo sus hojas.",
        "Marca las casillas de las hojas a liquidar (debe ser el mismo conductor; la app te avisará si no).",
        "Pulsa “Liquidar seleccionadas”; las hojas salen del listado y podrás revisarlas en Turnos Liquidados/Liquidaciones.",
        "Si necesitas borrar una hoja, usa el menú de acciones (···) > Eliminar.",
      ],
    },
    {
      title: "Gestionar personas y flota",
      steps: [
        "Administrar Conductores: alta/edición de usuarios que pueden crear turnos.",
        "Licencias: registra o edita la licencia asociada al conductor y selecciónala al crear turnos.",
        "Vehículos: alta y detalle de cada coche, kilometraje y mantenimientos desde el menú de Vehículos.",
      ],
    },
    {
      title: "Consultar datos y detectar incidencias",
      steps: [
        "Panel de Control: KPIs mensuales, ranking de recaudación y accesos rápidos.",
        "Analítica: evolución de ingresos/gastos y comparativa por conductor/licencia.",
        "Mapa de calor: visualiza zonas con más actividad según los servicios registrados.",
        "Servicios Abonados: busca un viaje concreto por fecha, dirección o ID de servicio.",
      ],
    },
  ];

  const ubicaciones = [
    { title: "Hojas de Turno", desc: "Listados, filtro por conductor, liquidación masiva y detalle (··· > Más detalles)." },
    { title: "Turnos Liquidados", desc: "Histórico de hojas ya cerradas; usa para comprobar que todo se movió bien." },
    { title: "Liquidaciones", desc: "Resumen por licencia con totales empresa, tarjetas, gasolina y km; botón Ver detalles." },
    { title: "Servicios Abonados", desc: "Histórico de viajes asociados a los turnos cargados (buscador por texto/fecha)." },
    { title: "Analítica / Mapa de calor", desc: "Reportes y visualización espacial para optimizar rutas y horarios." },
    { title: "Mi Usuario", desc: "Datos personales, teléfono y dirección usados en los listados y liquidaciones." },
  ];

  const preguntasFrecuentes = [
    {
      pregunta: "¿Cómo añado un nuevo conductor?",
      respuesta:
        "Ve a Administrar Conductores y pulsa en 'Añadir Conductor'. Completa el formulario y guarda; el usuario podrá iniciar sesión y crear turnos.",
    },
    {
      pregunta: "¿Dónde consulto los detalles de un servicio?",
      respuesta:
        "En Hojas de Turno usa el menú (···) > Más detalles para ver todos los servicios, o entra en Servicios Abonados para buscar por fecha, dirección o ID.",
    },
    {
      pregunta: "¿Qué significan los colores en los gráficos de Analítica?",
      respuesta:
        "Cada serie representa un concepto (ingresos, gastos, conductor o licencia). Pasa el ratón por la leyenda para ver la cifra exacta.",
    },
    {
      pregunta: "No puedo liquidar varias hojas",
      respuesta:
        "Solo se pueden liquidar hojas del mismo conductor. Filtra por conductor o asegúrate de marcar hojas asociadas a la misma persona.",
    },
    {
      pregunta: "¿Puedo modificar mis datos de usuario?",
      respuesta:
        "Sí, en la sección Perfil de Usuario puedes editar nombre, dirección, teléfono y más.",
    },
    {
      pregunta: "¿Cómo cierro una liquidación?",
      respuesta:
        "Liquidando las hojas desde Hojas de Turno. Las liquidaciones se recalculan automáticamente; consulta el detalle desde el botón 'Ver detalles' en la sección Liquidaciones.",
    },
    {
      pregunta: "Los totales no cuadran con mi hoja física",
      respuesta:
        "Revisa que recaudación inicial/final y kilómetros estén cargados sin separadores de miles, y que gasolina, tarjeta y crédito estén informados. Si importaste un CSV, edita los campos antes de guardar.",
    },
  ];

  return (
    <Box m="20px">
      <Header title="Ayuda" subtitle="Centro de ayuda y preguntas frecuentes" />

      <Grid container spacing={2}>
        {guiasRapidas.map((item, idx) => (
          <Grid item xs={12} md={4} key={idx}>
            <Card title={item.title}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Chip label={item.chip} size="small" color="secondary" />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Pasos rápidos
                </Typography>
              </Stack>
              <BulletList items={item.bullets} />
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box mt={3}>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
          Flujos principales
        </Typography>
        <Grid container spacing={2}>
          {flujos.map((flujo, idx) => (
            <Grid item xs={12} md={6} key={idx}>
              <Card title={flujo.title}>
                <BulletList items={flujo.steps} />
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box mt={3}>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
          ¿Dónde está cada cosa?
        </Typography>
        <Grid container spacing={2}>
          {ubicaciones.map((item, idx) => (
            <Grid item xs={12} md={4} key={idx}>
              <Card title={item.title}>
                <Typography>{item.desc}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box mt={3}>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
          Preguntas frecuentes
        </Typography>
        {preguntasFrecuentes.map((faq, index) => (
          <Accordion
            key={index}
            sx={{
              backgroundColor: theme.palette.custom.serviceCardBackground,
              borderRadius: 2,
              mb: 1.5,
              boxShadow: theme.palette.mode === "dark" ? 4 : 1,
              border: `1px solid ${theme.palette.mode === "dark" ? "#2f3b52" : "#d8d8d8"}`,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">{faq.pregunta}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>{faq.respuesta}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default HelpPage;
